// Window-manager store — ephemerální UI stav oken (režim, geometrie, z-order, fokus).
//
// Singleton Zustand store (na rozdíl od navigačního storu, který je per-Provider).
// Z-order je řešen **per stack** (plocha i každá floating skupina mají rank); kliknutí
// přenese celý stack do popředí. V rámci stacku je pořadí dané lineárním stackem.
// Geometrie je in-memory (po reloadu se logický stack obnoví z URL, geometrie dostane
// default). Perzistence layoutu do localStorage je samostatný krok (klíčování přes stackId).

import { create } from 'zustand';

import {
  PLOCHA_STACK_KEY,
  type WindowAnim,
  type WindowMode,
  type WindowRect,
  type WindowUiState,
} from './windowManagerTypes';

interface WindowManagerState {
  /** UI stav per layerId (režim, geometrie, animace). */
  readonly windows: Readonly<Record<string, WindowUiState>>;
  /** Z-rank per stack (klíč = stackId nebo PLOCHA_STACK_KEY); vyšší = víc vpředu. */
  readonly stackZ: Readonly<Record<string, number>>;
  /**
   * Původ floating stacku: nový stackId (vzniklý osamostatněním ZE stacku) → stackId, ze kterého
   * vznikl. Slouží pro dock „zpět do původního stacku". Osamostatnění Z PLOCHY origin neukládá
   * (chybějící záznam = vrátit na plochu).
   */
  readonly stackOrigin: Readonly<Record<string, string>>;
  /** Aktuálně fokusované okno (globální fokus pro zvýraznění). */
  readonly activeLayerId: string | null;
  readonly stackZCounter: number;
  /** Rozměr plochy (px) — pro výpočet výchozí geometrie při osamostatnění. */
  readonly desktopSize: { w: number; h: number };

  setActive: (layerId: string | null) => void;
  /** Zaznamená původ floating stacku (pro dock zpět do původního stacku). */
  setStackOrigin: (stackId: string, origin: string) => void;
  /** Přenese celý stack do popředí (nad všechny ostatní stacky). */
  raiseStack: (stackKey: string) => void;
  /** Zajistí z-rank pro nové stacky (zachová pořadí výskytu). */
  ensureStacks: (stackKeys: readonly string[]) => void;
  setMode: (layerId: string, mode: WindowMode) => void;
  setRect: (layerId: string, rect: WindowRect) => void;
  /** Hromadná aktualizace geometrie (drag celé skupiny — jeden re-render). */
  setRects: (entries: ReadonlyArray<readonly [string, WindowRect]>) => void;
  setAnim: (layerIds: readonly string[], anim: WindowAnim) => void;
  minimize: (layerId: string) => void;
  maximize: (layerId: string) => void;
  restore: (layerId: string) => void;
  ensureDefaults: (layerIds: readonly string[]) => void;
  prune: (layerIds: readonly string[]) => void;
  setDesktopSize: (size: { w: number; h: number }) => void;
}

const DEFAULT_STATE: WindowUiState = { mode: 'docked' };

export const useWindowManagerStore = create<WindowManagerState>((set, get) => ({
  windows: {},
  stackZ: { [PLOCHA_STACK_KEY]: 1 },
  stackOrigin: {},
  activeLayerId: null,
  stackZCounter: 1,
  desktopSize: { w: 1280, h: 640 },

  setActive: (layerId) => set({ activeLayerId: layerId }),

  setStackOrigin: (stackId, origin) =>
    set((s) => ({ stackOrigin: { ...s.stackOrigin, [stackId]: origin } })),

  raiseStack: (stackKey) =>
    set((s) => {
      const z = s.stackZCounter + 1;
      return { stackZCounter: z, stackZ: { ...s.stackZ, [stackKey]: z } };
    }),

  ensureStacks: (stackKeys) => {
    const missing = stackKeys.filter((k) => get().stackZ[k] === undefined);
    if (missing.length === 0) return;
    set((s) => {
      const next = { ...s.stackZ };
      let z = s.stackZCounter;
      for (const k of missing) next[k] = ++z;
      return { stackZ: next, stackZCounter: z };
    });
  },

  setMode: (layerId, mode) =>
    set((s) => {
      const prev = s.windows[layerId] ?? DEFAULT_STATE;
      return { windows: { ...s.windows, [layerId]: { ...prev, mode } } };
    }),

  setRect: (layerId, rect) =>
    set((s) => {
      const prev = s.windows[layerId] ?? DEFAULT_STATE;
      return { windows: { ...s.windows, [layerId]: { ...prev, rect } } };
    }),

  setRects: (entries) =>
    set((s) => {
      const next = { ...s.windows };
      for (const [id, rect] of entries) {
        next[id] = { ...(next[id] ?? DEFAULT_STATE), rect };
      }
      return { windows: next };
    }),

  setAnim: (layerIds, anim) =>
    set((s) => {
      const next = { ...s.windows };
      for (const id of layerIds) {
        next[id] = { ...(next[id] ?? DEFAULT_STATE), anim };
      }
      return { windows: next };
    }),

  minimize: (layerId) =>
    set((s) => {
      const prev = s.windows[layerId] ?? DEFAULT_STATE;
      return {
        windows: {
          ...s.windows,
          [layerId]: { ...prev, prevMode: prev.mode, prevRect: prev.rect, mode: 'minimized' },
        },
      };
    }),

  maximize: (layerId) =>
    set((s) => {
      const prev = s.windows[layerId] ?? DEFAULT_STATE;
      return {
        windows: { ...s.windows, [layerId]: { ...prev, prevRect: prev.rect, mode: 'maximized' } },
      };
    }),

  restore: (layerId) =>
    set((s) => {
      const prev = s.windows[layerId] ?? DEFAULT_STATE;
      return {
        windows: {
          ...s.windows,
          [layerId]: {
            ...prev,
            mode: prev.prevMode && prev.prevMode !== 'minimized' ? prev.prevMode : 'floating',
            rect: prev.prevRect ?? prev.rect,
          },
        },
      };
    }),

  ensureDefaults: (layerIds) => {
    const missing = layerIds.filter((id) => !get().windows[id]);
    if (missing.length === 0) return;
    set((s) => {
      const next = { ...s.windows };
      for (const id of missing) next[id] = { mode: 'docked' };
      return { windows: next };
    });
  },

  prune: (layerIds) => {
    const keep = new Set(layerIds);
    set((s) => {
      const ids = Object.keys(s.windows);
      if (ids.every((id) => keep.has(id))) return s;
      const next: Record<string, WindowUiState> = {};
      for (const id of ids) if (keep.has(id)) next[id] = s.windows[id] as WindowUiState;
      const activeLayerId = s.activeLayerId && keep.has(s.activeLayerId) ? s.activeLayerId : null;
      return { windows: next, activeLayerId };
    });
  },

  setDesktopSize: (size) =>
    set((s) =>
      s.desktopSize.w === size.w && s.desktopSize.h === size.h ? s : { desktopSize: size }
    ),
}));
